// src/utils/progressiveLoadingUtils.js
import moment from 'moment';

/**
 * 날짜 범위를 사용자 친화적 형식으로 포맷
 * @param {string} startDate - 시작 날짜 (ISO string)
 * @param {string} endDate - 끝 날짜 (ISO string)
 * @returns {string} 포맷된 날짜 범위
 */
export const formatDateRange = (startDate, endDate) => {
  const start = moment(startDate).format('YYYY.MM.DD');
  const end = moment(endDate).format('YYYY.MM.DD');
  return `${end} ~ ${start}`;
};

/**
 * 데이터의 바이트 크기 계산
 * @param {any} data - 크기를 계산할 데이터
 * @returns {number} 바이트 크기
 */
export const calculateDataSize = (data) => {
  return new Blob([JSON.stringify(data)]).size;
};

/**
 * JSON 문자열 길이로 간단한 크기 계산 (더 빠름)
 * @param {any} data - 크기를 계산할 데이터
 * @returns {number} 문자열 길이 (근사 바이트)
 */
export const calculateSize = (data) => {
  return JSON.stringify(data).length;
};

/**
 * 청크 저장이 필요한지 판단
 * @param {Array} threads - 스레드 배열
 * @param {number} threshold - 임계값 (기본: 950KB)
 * @returns {boolean} 저장 필요 여부
 */
export const shouldSaveChunk = (threads, threshold = 950 * 1024) => {
  return calculateSize(threads) > threshold;
};

/**
 * 전체 페이지 수 추정
 * @param {number} firstPageSize - 첫 페이지 크기
 * @param {number} totalThreadsHint - 전체 스레드 수 힌트
 * @returns {number} 예상 페이지 수
 */
export const estimateTotalPages = (firstPageSize, totalThreadsHint) => {
  if (!totalThreadsHint || !firstPageSize) return 10; // 기본값
  return Math.ceil(totalThreadsHint / firstPageSize);
};

/**
 * 진행률 계산
 * @param {number} current - 현재 진행
 * @param {number} total - 전체
 * @returns {number} 진행률 (0-100)
 */
export const calculateProgress = (current, total) => {
  if (!total || total === 0) return 0;
  return Math.min((current / total) * 100, 100);
};

/**
 * 진행 상황 데이터 생성
 * @param {string} stage - 진행 단계
 * @param {object} additionalData - 추가 데이터
 * @returns {object} 진행 상황 객체
 */
export const createProgressData = (stage, additionalData = {}) => {
  const baseData = {
    stage,
    timestamp: new Date().toISOString()
  };
  
  return { ...baseData, ...additionalData };
};

/**
 * 스레드 저장 필터 (텍스트 또는 미디어 있는 것만)
 * @param {Array} threads - 스레드 배열
 * @returns {Array} 필터된 스레드 배열
 */
export const filterThreadsForStorage = (threads) => {
  return threads.filter((thread) => {
    const hasText = !!thread.text;
    const hasMedia = !!thread.media_url;
    const hasChildrenMedia = !!(
      thread.children &&
      thread.children.data &&
      Array.isArray(thread.children.data) &&
      thread.children.data.some(child => child.media_url)
    );
    return hasText || hasMedia || hasChildrenMedia;
  });
};

/**
 * 배치 크기를 네트워크 상태에 따라 조정
 * @returns {object} 배치 설정 { chunkSize, delay }
 */
export const getOptimalBatchSettings = () => {
  try {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      // 네트워크 타입에 따른 최적화
      switch (connection.effectiveType) {
        case '4g':
          return { chunkSize: 100, delay: 50 };
        case '3g':
          return { chunkSize: 50, delay: 100 };
        case '2g':
          return { chunkSize: 25, delay: 200 };
        default:
          return { chunkSize: 50, delay: 100 };
      }
    }
    
    // 기본값 (네트워크 정보 없을 때)
    return { chunkSize: 50, delay: 100 };
  } catch (error) {
    console.warn('Failed to detect network condition:', error);
    return { chunkSize: 50, delay: 100 };
  }
};